"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { IReviewMention } from "../types";

interface IMentionChipsProps {
  mentions: IReviewMention[];
  onSelect: (slug: string) => void;
}

/**
 * "Guests mention" chips. Mobile is the app's swipeable row; desktop is Airbnb's: a
 * heading, one row of larger chips, and round arrows to page through it.
 */
const MentionChips: React.FC<IMentionChipsProps> = ({ mentions, onSelect }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update, mentions.length]);

  const page = (direction: 1 | -1) =>
    rowRef.current?.scrollBy({
      left: direction * rowRef.current.clientWidth * 0.8,
      behavior: "smooth",
    });

  const arrow = (direction: 1 | -1) => (
    <button
      type="button"
      aria-label={direction > 0 ? "More topics" : "Previous topics"}
      onClick={() => page(direction)}
      className={`absolute top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#DDDDDD] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 md:flex ${
        direction > 0 ? "-right-4" : "-left-4"
      }`}
    >
      {direction > 0 ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
    </button>
  );

  return (
    <div className="border-b border-[#EBEBEB] pb-6 md:border-b-0 md:pb-2">
      <h3 className="mb-6 hidden text-22 font-semibold text-ink md:block">
        Guests mention
      </h3>
      <div className="relative">
        {canScroll.left && arrow(-1)}
        <div
          ref={rowRef}
          onScroll={update}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 scrollbar-hide md:mx-0 md:gap-3 md:px-1 md:py-1"
        >
          {mentions.map((chip) => (
            <button
              key={chip.slug}
              type="button"
              onClick={() => onSelect(chip.slug)}
              className="flex shrink-0 items-center gap-2 rounded-xl border border-[#EBEBEB] bg-white px-3 py-2 text-sm font-medium text-ink transition-colors hover:border-[#222222] md:gap-2.5 md:border-[#DDDDDD] md:px-4 md:py-3.5 md:text-15 md:font-semibold md:shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            >
              {chip.icon_url && (
                <img src={chip.icon_url} alt="" className="h-5 w-5" />
              )}
              {chip.label}
              <span className="font-normal text-ink-secondary">
                {chip.count}
              </span>
            </button>
          ))}
        </div>
        {canScroll.right && arrow(1)}
      </div>
    </div>
  );
};

export default MentionChips;
